// src/pages/landing/PublicLandingPage.tsx
// /l/:slug — public, unauthenticated rendering of a published landing page.
// GetLandingPageBySlug. Also serves as the "preview" target from the editor.

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useI18n } from '@react/hooks';
import { Button, Input, Alert } from '@react/components';
import { landingApi } from '@core/api';
import type { LandingPage } from '@core/types/landing';
import { SectionRenderer, errMessage } from '@/features/landing';

export function PublicLandingPage() {
  const { t } = useI18n();
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState<LandingPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [password, setPassword] = useState('');

  const load = (pw?: string) => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    landingApi
      .GetLandingPageBySlug(slug, pw ? { params: { password: pw } } : undefined)
      .then((res) => {
        setPage(res.page ?? null);
        setNeedsPassword(false);
      })
      .catch((e: unknown) => {
        const msg = errMessage(e, t('landing.public.loadError'));
        if (/password/i.test(msg)) {
          setNeedsPassword(true);
        } else {
          setError(msg);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => load(), [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-primary" aria-label={t('common.loading')} />
      </div>
    );
  }

  if (needsPassword) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="w-full max-w-sm space-y-3 rounded-md border border-border p-6">
          <h1 className="text-lg font-semibold text-foreground">{t('landing.public.passwordTitle')}</h1>
          <Input
            type="password"
            label={t('landing.settings.password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load(password)}
          />
          <Button className="w-full" onClick={() => load(password)}>
            {t('common.submit')}
          </Button>
        </div>
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <Alert variant="error">{error ?? t('landing.public.notFound')}</Alert>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: page.theme?.background_color, color: page.theme?.text_color, fontFamily: page.theme?.font_family }} className="min-h-screen">
      {page.theme?.custom_css && <style>{page.theme.custom_css}</style>}
      {(page.sections ?? []).map((s) => (
        <SectionRenderer key={s.id} section={s} />
      ))}
      {page.settings?.show_branding !== false && (
        <footer className="border-t border-border py-4 text-center text-xs text-muted-foreground">{t('landing.public.poweredBy')}</footer>
      )}
    </div>
  );
}
